import {
  Button,
  Text,
  UseDisclosureReturn,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { SYLA_EARLIEST_EVENT_DATE } from "@syla/shared/types/helpers/constants";
import { addYears } from "date-fns";
import React, { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import {
  addYearsInTimezone,
  startOfHourInTimeZone,
  startOfMinuteInTimeZone,
} from "../../../components/atoms/datePicker/tzDateUtils";
import { DatePickerField } from "../../../components/forms/DatePickerField";
import { SingleSelectorField } from "../../../components/forms/SingleSelectorField";
import { FormModal } from "../../../components/modals/FormModal";
import { StandardSelectOption } from "../../../components/selector/parts/SelectOption";
import { AccountTimeZone } from "../../../constants/AccountTimeZone";
import { useMutationCreateManualSubscription } from "../../../store/useMutationCreateManualSubscription";
import { useQueryGetPlans } from "../../../store/useQueryGetPlans";

type CreateManualSubscriptionFormValues = {
  planId: string;
  expiresOn: Date;
};

export function CreateManualSubscriptionButton() {
  const disclosure = useDisclosure();

  return (
    <>
      <Button
        variant="outline"
        colorScheme="gray"
        backgroundColor="white"
        size="sm"
        onClick={disclosure.onOpen}
      >
        Create manual subscription
      </Button>

      {disclosure.isOpen && (
        <CreateManualSubscriptionModal disclosure={disclosure} />
      )}
    </>
  );
}

function CreateManualSubscriptionModal({
  disclosure,
}: {
  disclosure: UseDisclosureReturn;
}): React.ReactElement {
  const form = useForm<CreateManualSubscriptionFormValues>({
    mode: "onChange",
    defaultValues: {
      planId: "",
      expiresOn: startOfHourInTimeZone(
        addYearsInTimezone(new Date(), 1, AccountTimeZone),
        AccountTimeZone,
      ),
    },
  });

  const createManualSubscription = useMutationCreateManualSubscription();
  const plans = useQueryGetPlans();

  const planOptions: StandardSelectOption[] = useMemo(
    () =>
      (plans.data?.plans ?? [])
        .filter((plan) => plan.active)
        .map((plan) => ({
          value: plan._id,
          data: {
            label: plan.name,
          },
        })),
    [plans.data],
  );

  useEffect(() => {
    if (planOptions.length === 0) {
      return;
    }

    const currentPlanId = form.getValues("planId");

    if (currentPlanId || form.formState.dirtyFields.planId) {
      return;
    }

    const defaultPlan =
      planOptions.find((plan) => plan.data.label === "Private Wealth") ??
      planOptions[0];

    form.setValue("planId", defaultPlan.value, {
      shouldValidate: true,
      shouldDirty: false,
      shouldTouch: false,
    });
  }, [planOptions, form]);

  const isPlansReady = planOptions.length > 0;

  const onSubmit = async (data: CreateManualSubscriptionFormValues) => {
    await createManualSubscription.mutateAsync({
      planId: data.planId,
      expiresOn: data.expiresOn,
    });
  };

  return (
    <FormModal
      disclosure={disclosure}
      form={form}
      title="Create manual subscription"
      submitButtonText="Create plan"
      successTitle="Subscription created"
      errorTitle="Create subscription failed"
      onSubmit={onSubmit}
    >
      <Text color="gray.600" mb={4}>
        Create a manual unpaid subscription for this account.
      </Text>

      <Text color="gray.600" mb={6}>
        This is for internal use only and cannot be applied if the account
        already has an active Stripe-managed subscription.
      </Text>

      <VStack width="full" align="stretch" spacing={6}>
        <SingleSelectorField
          form={form}
          name="planId"
          label="Plan type"
          options={planOptions}
          placeholder={isPlansReady ? "Select a plan" : "Loading plans..."}
          isRequired
          isDisabled={!isPlansReady}
          buttonProps={{
            width: "full",
          }}
          popoverProps={{
            matchWidth: true,
          }}
        />

        <DatePickerField
          form={form}
          name="expiresOn"
          label="Plan expiration date"
          isRequired
          showTime={true}
          timeZone={AccountTimeZone}
          earliestDate={SYLA_EARLIEST_EVENT_DATE}
          latestDate={startOfMinuteInTimeZone(
            addYears(new Date(), 10),
            AccountTimeZone,
          )}
        />
      </VStack>
    </FormModal>
  );
}
